import React from 'react'
import { CircularProgress } from '@mui/material'
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material'
import { api } from '../../function/axios'
import s from './UserPicker.module.css'

/*
 * Pick a user instead of typing a uid. Searches the same list the
 * Subscribers page reads (/users/clients, its `search` param — name / e-mail),
 * shows name, e-mail and uid for each hit, and hands back the chosen uid.
 * A pasted uid still works: what is typed is the value until a user is picked.
 *
 * The results open in the flow under the field, not as a floating layer, so a
 * dialog with overflow: hidden never clips them.
 */

export type User = { id: number; uid?: string; email?: string; firstname?: string; lastname?: string; avatar?: string }

const LIMIT = 8
const nameOf = (u: User) => [u.firstname, u.lastname].filter(Boolean).join(' ')
const initials = (u: User) => {
  const n = nameOf(u)
  const parts = (n || u.email || '?').split(/[\s@.]+/).filter(Boolean)
  return parts.slice(0, 2).map((p) => p[0]).join('').toUpperCase() || '?'
}

export function UserPicker({ value, onChange, invalid, placeholder = 'Search by name or e-mail, or paste a uid' }: {
  /** the chosen uid */
  value: string
  /** `user` is set when the uid was picked from the list (so it exists), not typed */
  onChange: (uid: string, user?: User) => void
  invalid?: boolean
  placeholder?: string
}) {
  const [query, setQuery] = React.useState(value)
  const [picked, setPicked] = React.useState<User | null>(null)
  const [hits, setHits] = React.useState<User[]>([])
  const [loading, setLoading] = React.useState(false)
  const [open, setOpen] = React.useState(false)

  React.useEffect(() => {
    const q = query.trim()
    if (picked || q.length < 2) { setHits([]); setLoading(false); return undefined }
    let cancelled = false
    setLoading(true)
    const id = setTimeout(() => {
      api({ method: 'GET', url: '/users/clients', params: { offset: 0, limit: LIMIT, search: q } })
        .then(({ data }) => {
          if (cancelled) return
          setHits(Array.isArray(data) ? data : (data?.list || []))
        })
        .catch(() => { if (!cancelled) setHits([]) })
        .finally(() => { if (!cancelled) setLoading(false) })
    }, 300)
    return () => { cancelled = true; clearTimeout(id) }
  }, [query, picked])

  const pick = (u: User) => {
    setPicked(u)
    setOpen(false)
    onChange(u.uid || '', u)
  }
  const clear = () => {
    setPicked(null)
    setQuery('')
    onChange('')
  }

  if (picked) {
    return (
      <div className={`${s.picked} ${invalid ? s.invalid : ''}`}>
        <span className={s.ava}>{initials(picked)}</span>
        <span className={s.who}>
          <b>{nameOf(picked) || picked.email || `#${picked.id}`}</b>
          <span>{picked.email}{picked.uid ? ` · ${picked.uid}` : ''}</span>
        </span>
        <button type="button" className={s.clear} onClick={clear} aria-label="Choose another user"><CloseIcon sx={{ fontSize: 16 }} /></button>
      </div>
    )
  }

  const q = query.trim()
  return (
    <div className={s.wrap}>
      <label className={`${s.box} ${invalid ? s.invalid : ''}`}>
        <SearchIcon sx={{ fontSize: 17, color: 'var(--text3)' }} />
        <input
          value={query}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); onChange(e.target.value.trim()) }}
        />
        {loading && <CircularProgress size={14} thickness={5} sx={{ color: 'var(--text3)' }} />}
      </label>
      {open && q.length >= 2 && !loading && (
        <div className={s.list}>
          {hits.length ? hits.map((u) => (
            <button key={u.id} type="button" className={s.hit} onClick={() => pick(u)}>
              <span className={s.ava}>{initials(u)}</span>
              <span className={s.who}>
                <b>{nameOf(u) || u.email || `#${u.id}`}</b>
                <span>{u.email}</span>
              </span>
              <span className={s.uid}>{u.uid}</span>
            </button>
          )) : (
            <div className={s.none}>No users match “{q}”. A pasted uid is used as typed.</div>
          )}
        </div>
      )}
    </div>
  )
}
